// My Briefing: one person's morning page (asked 2026-10-24). Where you left off on
// your last day worked, what's on today, your to-dos, and what came in since. The
// lists load first; the written recap fills in after, since it takes a few seconds.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiSun, FiClock, FiAlertTriangle, FiCheckSquare, FiMapPin, FiList, FiBell, FiZap } from 'react-icons/fi';
import { getMyBriefing, markMyBriefingSeen } from '../services/api';

// Date-only strings are calendar dates: parse as local, never append 'Z'.
const fmtDay = (d) => new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
const fmtTime = (iso) => iso ? new Date(iso.endsWith('Z') ? iso : iso + 'Z').toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';
const hrs = (h) => `${Math.round(h * 10) / 10} hr${h === 1 ? '' : 's'}`;

const card = { background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, marginBottom: 12 };
const sectionTitle = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '22px 0 10px' };
const LOCATE_COLORS = { red: ['#fef2f2', '#b91c1c'], yellow: ['#fefce8', '#a16207'], green: ['#f0fdf4', '#15803d'] };
const NOTICE_LABELS = { mention: 'Mentioned you', document: 'New document', assignment: 'Assigned to you', todo_assigned: 'New to-do', sms_reply: 'Client text', client_request: 'Client request' };

function JobLink({ job }) {
  return (
    <Link to={`/jobs/${job.id}`} style={{ fontWeight: 700, fontSize: 15, color: '#1d4ed8', textDecoration: 'none' }}>
      {job.job_number}
    </Link>
  );
}

function OpenItems({ items, label = 'Still open on the checklist' }) {
  if (!items || items.length === 0) return null;
  const shown = items.slice(0, 8);
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>{label}</div>
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {shown.map((i, k) => (
          <li key={k} style={{ fontSize: 13, color: i.important ? '#991b1b' : '#374151', fontWeight: i.important ? 600 : 400, marginBottom: 3 }}>
            {i.text}
          </li>
        ))}
      </ul>
      {items.length > shown.length && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>…and {items.length - shown.length} more</div>}
    </div>
  );
}

function WorkOrders({ list }) {
  if (!list || list.length === 0) return null;
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Open work orders</div>
      {list.map((w, k) => (
        <div key={k} style={{ fontSize: 13, color: '#991b1b', fontWeight: 600, marginBottom: 3 }}>
          {w.order_number ? `${w.order_number}: ` : ''}{w.description}
          <span style={{ fontWeight: 400, color: '#6b7280' }}> · {w.status}</span>
        </div>
      ))}
    </div>
  );
}

function Empty({ children }) {
  return <div style={{ ...card, color: '#6b7280', fontSize: 14 }}>{children}</div>;
}

export default function MyBriefing() {
  const [data, setData] = useState(null);
  const [recap, setRecap] = useState(null);
  const [recapLoading, setRecapLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    markMyBriefingSeen().catch(() => {});
    getMyBriefing(false)
      .then(res => { if (alive) setData(res.data); })
      .catch(() => { if (alive) setError('Could not load your briefing. Check your connection and pull down to refresh.'); });
    getMyBriefing(true)
      .then(res => { if (alive) { setRecap(res.data.recap); setData(prev => prev || res.data); } })
      .catch(() => {})
      .finally(() => { if (alive) setRecapLoading(false); });
    return () => { alive = false; };
  }, []);

  if (error && !data) return <div style={{ maxWidth: 800, margin: '0 auto', padding: 16, color: '#b91c1c' }}>{error}</div>;
  if (!data) return <div className="loading">Loading your briefing...</div>;

  const due = data.todos.filter(t => t.bucket !== 'later');
  const later = data.todos.filter(t => t.bucket === 'later');
  const hasAnything = data.last_jobs.length || data.today_jobs.length;

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 4px 40px' }}>
      <div style={{ margin: '4px 0 6px' }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
          <FiSun color="#f59e0b" /> Good morning{data.first_name ? `, ${data.first_name}` : ''}
        </h1>
        <div style={{ color: '#6b7280', fontSize: 14, marginTop: 2 }}>{fmtDay(data.date)}</div>
      </div>

      {(recapLoading || recap) && hasAnything ? (
        <div style={{ ...card, background: '#f0f9ff', borderColor: '#bae6fd', marginTop: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: '#0369a1', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            <FiZap /> Your day at a glance
          </div>
          <div style={{ fontSize: 15, lineHeight: 1.5, color: '#0f172a' }}>
            {recap || <span style={{ color: '#64748b' }}>Writing your summary…</span>}
          </div>
        </div>
      ) : null}

      {/* Where you left off */}
      <div style={sectionTitle}><FiClock /> Where you left off{data.last_day ? ` · ${fmtDay(data.last_day)}` : ''}</div>
      {!data.last_day && <Empty>No time on record yet. Once you clock in on a job, this shows where you left off.</Empty>}
      {data.last_day && data.last_jobs.length === 0 && <Empty>You clocked in that day but not on a specific job.</Empty>}
      {data.last_jobs.map(j => (
        <div key={j.job.id} style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <JobLink job={j.job} />
            <span style={{ fontSize: 13, color: '#6b7280' }}>
              {j.hours > 0 ? hrs(j.hours) : ''}{j.work_types.length ? ` · ${j.work_types.join(', ')}` : ''}
            </span>
          </div>
          {j.job.address && <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>{j.job.address}</div>}
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Status now: <b style={{ color: '#374151' }}>{j.job.status}</b></div>
          {j.notes.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Notes that day</div>
              {j.notes.map((n, k) => (
                <div key={k} style={{ fontSize: 13, color: '#374151', background: '#f9fafb', borderRadius: 8, padding: '8px 10px', marginBottom: 6, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  <span style={{ fontWeight: 600 }}>{n.author}</span>
                  <span style={{ color: '#9ca3af', fontSize: 11 }}> · {fmtTime(n.created_at)}</span>
                  <div>{n.text}</div>
                </div>
              ))}
            </div>
          )}
          <OpenItems items={j.open_items} />
          <WorkOrders list={j.open_work_orders} />
        </div>
      ))}

      {/* Today */}
      <div style={sectionTitle}><FiMapPin /> Today</div>
      {data.today_jobs.length === 0 && <Empty>Nothing on the schedule for you today. Check with the office if that seems wrong.</Empty>}
      {data.today_jobs.map(j => (
        <div key={j.job.id} style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <JobLink job={j.job} />
            {j.scheduled_time && <span style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>{j.scheduled_time}</span>}
          </div>
          {j.job.address && <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>{j.job.address}</div>}
          {j.job.client && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{j.job.client}</div>}
          {j.locate && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8, padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700,
              background: (LOCATE_COLORS[j.locate.level] || LOCATE_COLORS.yellow)[0], color: (LOCATE_COLORS[j.locate.level] || LOCATE_COLORS.yellow)[1] }}>
              {j.locate.level !== 'green' && <FiAlertTriangle />} {j.locate.label}
            </div>
          )}
          <OpenItems items={j.important_items} label="Important checklist items" />
          <WorkOrders list={j.open_work_orders} />
        </div>
      ))}

      {/* To-dos */}
      <div style={sectionTitle}><FiList /> Your to-dos</div>
      {data.todos.length === 0 && <Empty>No open to-dos.</Empty>}
      {due.length > 0 && (
        <div style={card}>
          {due.map(t => (
            <div key={t.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 6, fontSize: 14 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: t.bucket === 'overdue' ? '#b91c1c' : '#a16207', minWidth: 58 }}>
                {t.bucket === 'overdue' ? 'OVERDUE' : 'TODAY'}
              </span>
              <span style={{ color: '#111827' }}>
                {t.text}
                {t.related_job_number && <Link to={`/jobs/${t.related_job_id}`} style={{ fontSize: 12, marginLeft: 6 }}>{t.related_job_number}</Link>}
              </span>
            </div>
          ))}
        </div>
      )}
      {later.length > 0 && (
        <Link to="/todos" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
          <FiCheckSquare /> {later.length} more on your to-do list
        </Link>
      )}

      {/* Since you were last in */}
      {data.notices.length > 0 && (
        <>
          <div style={sectionTitle}><FiBell /> Since you were last in</div>
          <div style={card}>
            {data.notices.map(n => (
              <div key={n.id} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {NOTICE_LABELS[n.notification_type] || 'Notice'}
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>
                  {n.related_job_id ? <Link to={`/jobs/${n.related_job_id}`}>{n.title}</Link> : n.title}
                </div>
                {n.message && <div style={{ fontSize: 13, color: '#475569', overflowWrap: 'anywhere' }}>{n.message}</div>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
